import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Paper,
  Typography,
  Pagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Avatar
} from "@mui/material";
import { Snackbar, Alert } from "@mui/material";
import { 
  PersonAdd, 
  PersonRemove, 
  Visibility, 
  Edit,
  CheckCircle,
  Cancel
} from "@mui/icons-material";
import Header from "../../components/AdminNavebar";
import { getData , putData} from "../../services/Request";

const UserTable = () => {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const limit = 10; // per page users

  // ✅ API call to get all users
  const fetchUsers = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await getData(`api/auth/allUsers?page=${pageNum}&limit=${limit}`);
      setUsers(res.users);
      setTotalUsers(res.totalUsers);
      setTotalPages(res.totalPages);
      setPage(res.currentPage);
    } catch (error) {
      console.error("Error fetching users:", error);
      setSnackbar({ open: true, message: "Failed to fetch users", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(page);
  }, [page]);

  const toggleStatus = async (id, currentStatus) => {
    setActionLoading(id);
    try {
      const res = await putData(`api/auth/user/${id}/status`, {
        active: !currentStatus,
      });
  
      if (res.status) {
        setUsers((prev) =>
          prev.map((user) =>
            user.id === id ? { ...user, status: !user.status } : user
          )
        );
  
        setSnackbar({ open: true, message: res.message, type: "success" });
      } else {
        setSnackbar({ open: true, message: res.message, type: "error" });
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      setSnackbar({ open: true, message: "Failed to update status", type: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      <Header />
      <Snackbar
         open={snackbar.open}
         autoHideDuration={3000}
         onClose={() => setSnackbar({ ...snackbar, open: false })}
         anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
       >
         <Alert
           onClose={() => setSnackbar({ ...snackbar, open: false })}
           severity={snackbar.type}
           variant="filled"
         >
           {snackbar.message}
         </Alert>
       </Snackbar>
      <Box sx={{ 
        p: 4, 
        bgcolor: "#f8f9fa", 
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: "white", 
              fontWeight: "bold", 
              mb: 1,
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
            }}
          >
            User Management
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ color: "rgba(255,255,255,0.8)" }}
          >
            Manage and monitor user accounts
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: "linear-gradient(45deg, #4CAF50, #45a049)",
              color: "white",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
            }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {totalUsers}
                </Typography>
                <Typography variant="body1">
                  Total Users
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: "linear-gradient(45deg, #2196F3, #1976D2)",
              color: "white",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
            }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {users.filter(user => user.status).length}
                </Typography>
                <Typography variant="body1">
                  Active Users
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: "linear-gradient(45deg, #FF9800, #F57C00)",
              color: "white",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
            }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {users.filter(user => !user.status).length}
                </Typography>
                <Typography variant="body1">
                  Inactive Users
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: "linear-gradient(45deg, #9C27B0, #7B1FA2)",
              color: "white",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
            }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {totalPages}
                </Typography>
                <Typography variant="body1">
                  Total Pages
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Table */}
        <Paper sx={{ 
          overflow: "hidden", 
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(10px)"
        }}>
          {loading ? (
            <Box sx={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center", 
              height: 400 
            }}>
              <CircularProgress size={60} />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    background: "linear-gradient(45deg, #667eea, #764ba2)",
                    "& .MuiTableCell-head": {
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "1rem"
                    }
                  }}>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow 
                      key={user.id}
                      sx={{ 
                        "&:nth-of-type(odd)": {
                          backgroundColor: "rgba(0,0,0,0.02)"
                        },
                        "&:hover": {
                          backgroundColor: "rgba(102, 126, 234, 0.1)",
                          transform: "scale(1.01)",
                          transition: "all 0.2s ease-in-out"
                        },
                        transition: "all 0.2s ease-in-out"
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: user.status ? "#4CAF50" : "#f44336",
                            width: 40,
                            height: 40
                          }}>
                            {user.name?.charAt(0)?.toUpperCase() || "U"}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                              {user.name || "Unknown User"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {user.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={user.status ? <CheckCircle /> : <Cancel />}
                          label={user.status ? "Active" : "Inactive"}
                          color={user.status ? "success" : "error"}
                          variant="filled"
                          sx={{ 
                            fontWeight: "bold",
                            "& .MuiChip-icon": {
                              color: "white"
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                          <Tooltip title={user.status ? "Deactivate User" : "Activate User"}>
                            <IconButton
                              onClick={() => toggleStatus(user.id, user.status)}
                              disabled={actionLoading === user.id}
                              sx={{
                                bgcolor: user.status ? "#f44336" : "#4CAF50",
                                color: "white",
                                "&:hover": {
                                  bgcolor: user.status ? "#d32f2f" : "#388e3c",
                                  transform: "scale(1.1)"
                                },
                                transition: "all 0.2s ease-in-out"
                              }}
                            >
                              {actionLoading === user.id ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : user.status ? (
                                <PersonRemove />
                              ) : (
                                <PersonAdd />
                              )}
                            </IconButton>
                          </Tooltip>
                          {/* <Tooltip title="View Details">
                            <IconButton
                              sx={{
                                bgcolor: "#2196F3",
                                color: "white",
                                "&:hover": {
                                  bgcolor: "#1976D2",
                                  transform: "scale(1.1)"
                                },
                                transition: "all 0.2s ease-in-out"
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip> */}
                          {/* <Tooltip title="Edit User">
                            <IconButton
                              sx={{
                                bgcolor: "#FF9800",
                                color: "white",
                                "&:hover": {
                                  bgcolor: "#F57C00",
                                  transform: "scale(1.1)"
                                },
                                transition: "all 0.2s ease-in-out"
                              }}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip> */}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Paper>

        {/* Enhanced Pagination */}
        <Box sx={{ 
          mt: 4, 
          display: "flex", 
          justifyContent: "center",
          "& .MuiPaginationItem-root": {
            color: "white",
            "&.Mui-selected": {
              backgroundColor: "rgba(255,255,255,0.2)",
              color: "white"
            },
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)"
            }
          }
        }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      </Box>

     

    </>
  );
};

export default UserTable;
